import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCustomerGroupById, useCustomerGroupCounts, useCustomersByGroup } from '../../hooks/queries';
import {
  buildCustomerGroupListNavigation,
  isValidCustomerGroupId,
  type CustomerGroupDetailLocationState,
} from '../../utils/customer-navigation';

export function CustomerGroupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const listContext = location.state as CustomerGroupDetailLocationState | null;
  const [customerPage, setCustomerPage] = useState(1);
  const customerLimit = 50;

  const groupId = id ? parseInt(id, 10) : 0;

  const goBackToCustomerGroups = (): void => {
    const { path, state } = buildCustomerGroupListNavigation({
      highlightGroupId: isValidCustomerGroupId(groupId) ? groupId : undefined,
      page: listContext?.listPage,
      search: listContext?.listSearch,
      limit: listContext?.listLimit,
    });
    navigate(path, { state });
  };

  const { data: groupResponse, isLoading: loading, error: groupQueryError } = useCustomerGroupById(groupId);
  const group = groupResponse?.data ?? null;
  const error = groupQueryError
    ? String((groupQueryError as any).message ?? groupQueryError)
    : groupResponse?.success === false
    ? groupResponse.message
    : null;

  const { data: countsResponse, isLoading: loadingCount } = useCustomerGroupCounts(
    isValidCustomerGroupId(groupId) ? [groupId] : [],
  );
  const customerCount: number | null = countsResponse?.data?.[groupId] ?? null;

  const { data: customersResponse, isLoading: loadingCustomers } = useCustomersByGroup(groupId, customerPage, customerLimit);
  const customers: any[] = Array.isArray(customersResponse?.data) ? customersResponse.data : [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="page-container" style={{ textAlign: 'center' }}>
        <p>Loading customer group details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div
          style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            padding: '1rem',
            color: '#c33',
            marginBottom: '1rem',
          }}
        >
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={goBackToCustomerGroups}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#3498db',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Back to Customer Groups
        </button>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={goBackToCustomerGroups}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#95a5a6',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '1rem',
          }}
        >
          ← Back to Customer Groups
        </button>
        <h1 style={{ margin: 0 }}>{group.name || `Customer Group #${group.id}`}</h1>
      </div>

      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #eee',
          }}
        >
          <h2 style={{ margin: 0 }}>Group Information</h2>
          {group.is_default && (
            <span
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                backgroundColor: '#2ecc71',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            >
              Default Group
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <strong>Group ID:</strong>
            <p style={{ margin: '0.5rem 0', fontFamily: 'monospace' }}>{group.id}</p>
          </div>
          <div>
            <strong>Is Default:</strong>
            <p style={{ margin: '0.5rem 0' }}>{group.is_default ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <strong>Registered Customers:</strong>
            <p style={{ margin: '0.5rem 0' }}>
              {loadingCount ? 'Loading...' : customerCount !== null ? customerCount : 'N/A'}
            </p>
          </div>
        </div>

        {group.description && (
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #eee' }}>
            <strong>Description:</strong>
            <p style={{ margin: '0.5rem 0', color: '#666' }}>{group.description}</p>
          </div>
        )}

        {group.discount_rules && group.discount_rules.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginTop: 0 }}>Discount Rules</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {group.discount_rules.map((rule: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    padding: '1rem',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    border: '1px solid #eee',
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                    <div>
                      <strong>Type:</strong>
                      <p style={{ margin: '0.5rem 0' }}>
                        {rule.type === 'price_list' && 'Price List'}
                        {rule.type === 'percentage' && 'Percentage Discount'}
                        {rule.type === 'fixed' && 'Fixed Discount'}
                      </p>
                    </div>
                    {rule.amount !== undefined && (
                      <div>
                        <strong>Amount:</strong>
                        <p style={{ margin: '0.5rem 0', fontSize: '1.1rem', fontWeight: 600, color: '#2ecc71' }}>
                          {rule.type === 'percentage' && `${rule.amount}%`}
                          {rule.type === 'fixed' && formatCurrency(rule.amount)}
                          {rule.type === 'price_list' && rule.amount}
                        </p>
                      </div>
                    )}
                  </div>
                  {rule.name && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <strong>Name:</strong>
                      <span style={{ marginLeft: '0.5rem' }}>{rule.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(!group.discount_rules || group.discount_rules.length === 0) && (
          <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666' }}>
            No discount rules configured for this group
          </div>
        )}
      </div>

      {/* Customers Section */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1.5rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h2 style={{ margin: 0 }}>Customers</h2>
          {customerCount !== null && (
            <div
              style={{
                fontSize: '0.9rem',
                color: '#666',
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
              }}
            >
              <span>
                Showing {customers.length > 0 ? (customerPage - 1) * customerLimit + 1 : 0}-
                {Math.min(customerPage * customerLimit, customerCount || 0)} of {customerCount || 0}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  onClick={() => setCustomerPage(customerPage - 1)}
                  disabled={customerPage === 1}
                  style={{
                    padding: '0.25rem 0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: customerPage === 1 ? '#f5f5f5' : '#fff',
                    cursor: customerPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: customerPage === 1 ? 0.6 : 1,
                    fontSize: '0.85rem',
                  }}
                >
                  ←
                </button>
                <span style={{ fontSize: '0.85rem' }}>Page {customerPage}</span>
                <button
                  onClick={() => setCustomerPage(customerPage + 1)}
                  disabled={customers.length < customerLimit || (customerCount !== null && customerPage * customerLimit >= customerCount)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: (customers.length < customerLimit || (customerCount !== null && customerPage * customerLimit >= customerCount)) ? '#f5f5f5' : '#fff',
                    cursor: (customers.length < customerLimit || (customerCount !== null && customerPage * customerLimit >= customerCount)) ? 'not-allowed' : 'pointer',
                    opacity: (customers.length < customerLimit || (customerCount !== null && customerPage * customerLimit >= customerCount)) ? 0.6 : 1,
                    fontSize: '0.85rem',
                  }}
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>

        {loadingCustomers ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            Loading customers...
          </div>
        ) : customers.length === 0 ? (
          <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666' }}>
            No customers found in this group
          </div>
        ) : (
          <div
            style={{
              overflowX: 'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem',
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    ID
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Phone
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Date Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    style={{
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#666' }}>
                      {customer.id}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#2c3e50' }}>
                      {customer.first_name || customer.last_name
                        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                        : 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#666' }}>
                      {customer.email || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#666' }}>
                      {customer.phone || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#666' }}>
                      {customer.date_created
                        ? new Date(customer.date_created).toLocaleDateString()
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

